import { z } from 'zod';

import { preDefinedOrCustomFilterFieldNameSchema } from './filter-state';

// Mirror the table zoom constants in the ui package; shared must not import ui code.
export const TABLE_ZOOM_DEFAULT = 1;
export const TABLE_ZOOM_MIN = 0.5;
export const TABLE_ZOOM_MAX = 1.5;

const columnVisibilitySchema = z.record(z.string(), z.boolean());
const jobRecommendationsFilterSchema = z.record(
    z.string(),
    z.partialRecord(preDefinedOrCustomFilterFieldNameSchema, z.any()),
);
const instructiveTextSchema = z.record(z.string(), z.boolean());

export const gridSortModelSchema = z
    .object({
        field: z.string(),
        sort: z.enum(['asc', 'desc']).nullable(),
    })
    .array();

export const uiSettingsSchema = z
    .object({
        aiTransformationJobListColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        aiTransformationPositionListColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        apiKeysColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        dataAnonymization: z.boolean(),
        dismissedInstructiveText: z
            .string()
            .transform((jsonString) => instructiveTextSchema.catch({}).parse(JSON.parse(jsonString))),
        isFirstLoginPopupShown: z.boolean(),
        keyboardShortcutsEnabled: z.boolean(),
        jobArchitectureOnboardingReviewColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        jobArchitectureOverviewColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        jobArchitectureLevelComparisonHighlightProficiency: z.boolean(),
        jobArchitectureLevelComparisonShowProficiency: z.boolean(),
        jobListColumnOrder: z
            .string()
            .transform((jsonString) => z.string().array().catch([]).parse(JSON.parse(jsonString))),
        jobListColumnSorting: z
            .string()
            .transform((jsonString) => gridSortModelSchema.catch([]).parse(JSON.parse(jsonString))),
        jobListColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        jobProfileManagementColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        jobRecommendationsFilter: z
            .string()
            .transform((jsonString) => jobRecommendationsFilterSchema.catch({}).parse(JSON.parse(jsonString))),
        knowledgeBaseDocumentsColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        learningOpportunitiesColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        selectedJobArchitectureId: z.uuid().nullable().catch(null),
        showPublishPopup: z.boolean(),
        showReviewPopup: z.boolean(),
        skillCatalogColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
        tableZoom: z.number().min(TABLE_ZOOM_MIN).max(TABLE_ZOOM_MAX).catch(TABLE_ZOOM_DEFAULT),
        usersAndRolesColumnVisibility: z
            .string()
            .transform((jsonString) => columnVisibilitySchema.catch({}).parse(JSON.parse(jsonString))),
    })
    .partial();

export type UserUiSettings = z.infer<typeof uiSettingsSchema>;

export type SerializedUiSettings = Record<string, boolean | number | string | null>;

export const getKeyboardShortcutsEnabled = (settings: UserUiSettings): boolean =>
    settings.keyboardShortcutsEnabled ?? true;

export const getTableZoom = (settings: UserUiSettings): number => settings.tableZoom ?? TABLE_ZOOM_DEFAULT;
